import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@tonic/shared/components/ui';
import { DashboardChart } from '@tonic/shared/components/ui';
import { ArrowUpRight, ArrowDownRight, Calendar, DollarSign, Clock, Package, TrendingUp } from 'lucide-react';
import { useResaleAnalytics } from '@/features/re-commerce/api/hooks';
import { formatCurrency, formatNumber, formatPercent } from '@/utils/format';

const timeRanges = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '12m', label: 'Last 12 Months' },
];

export function ResaleAnalyticsPage() {
  const [timeRange, setTimeRange] = React.useState('30d');
  const { data, isLoading } = useResaleAnalytics({ timeRange });

  const metrics = data?.analytics || {
    salesTrend: [],
    categoryDistribution: [],
    conditionDistribution: [],
    priceRangeDistribution: [],
    timeToSell: [],
    profitMargin: []
  };

  // Calculate summary metrics
  const latestSales = metrics.salesTrend[metrics.salesTrend.length - 1]?.value || 0;
  const previousSales = metrics.salesTrend[metrics.salesTrend.length - 2]?.value || 0;
  const salesGrowth = ((latestSales - previousSales) / previousSales) * 100;

  const latestTimeToSell = metrics.timeToSell[metrics.timeToSell.length - 1]?.days || 0;
  const previousTimeToSell = metrics.timeToSell[metrics.timeToSell.length - 2]?.days || 0;
  const timeToSellImprovement = ((previousTimeToSell - latestTimeToSell) / previousTimeToSell) * 100;

  const totalItems = metrics.categoryDistribution.reduce((sum, item) => sum + item.value, 0);
  const likeNewItems = metrics.conditionDistribution.find(item => item.condition === 'Like New')?.value || 0;
  const likeNewPercentage = (likeNewItems / metrics.conditionDistribution.reduce((sum, item) => sum + item.value, 0)) * 100;

  const latestMargin = metrics.profitMargin[metrics.profitMargin.length - 1]?.value || 0;
  const previousMargin = metrics.profitMargin[metrics.profitMargin.length - 2]?.value || 0;
  const marginGrowth = latestMargin - previousMargin;

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-8 w-48 bg-neutral-800 animate-pulse rounded" />
          <div className="h-10 w-40 bg-neutral-800 animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="bg-neutral-800 animate-pulse h-[400px]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Analytics</h1>
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Sales</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(latestSales)}</div>
            <div className="flex items-center space-x-2">
              <Badge variant={salesGrowth >= 0 ? 'default' : 'destructive'}>
                {salesGrowth >= 0 ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                {Math.abs(salesGrowth).toFixed(1)}%
              </Badge>
              <span className="text-sm text-muted-foreground">vs. last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Time to Sell</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{latestTimeToSell} days</div>
            <div className="flex items-center space-x-2">
              <Badge variant={timeToSellImprovement >= 0 ? 'default' : 'destructive'}>
                {timeToSellImprovement >= 0 ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                {Math.abs(timeToSellImprovement).toFixed(1)}%
              </Badge>
              <span className="text-sm text-muted-foreground">faster than last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalItems}</div>
            <div className="flex items-center space-x-2">
              <Badge variant="secondary">
                {likeNewPercentage.toFixed(1)}%
              </Badge>
              <span className="text-sm text-muted-foreground">in like-new condition</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Profit Margin</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{latestMargin}%</div>
            <div className="flex items-center space-x-2">
              <Badge variant={marginGrowth >= 0 ? 'default' : 'destructive'}>
                {marginGrowth >= 0 ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                {Math.abs(marginGrowth).toFixed(1)}%
              </Badge>
              <span className="text-sm text-muted-foreground">vs. last month</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Sales Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Sales Trend"
              type="line"
              data={metrics.salesTrend}
              dataKey="value"
              xAxisKey="date"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Category Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Category Distribution"
              type="pie"
              data={metrics.categoryDistribution}
              dataKey="value"
              xAxisKey="category"
              height={300}
              colors={['#10B981', '#6366F1', '#F59E0B', '#3B82F6', '#EC4899']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Condition Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Condition Distribution"
              type="pie"
              data={metrics.conditionDistribution}
              dataKey="value"
              xAxisKey="condition"
              height={300}
              colors={['#10B981', '#6366F1', '#F59E0B', '#EF4444']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Price Range Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Price Range Distribution"
              type="bar"
              data={metrics.priceRangeDistribution}
              dataKey="count"
              xAxisKey="range"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Time to Sell</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Time to Sell"
              type="line"
              data={metrics.timeToSell}
              dataKey="days"
              xAxisKey="date"
              height={300}
              colors={['#F59E0B']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Profit Margin</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              title="Profit Margin"
              type="line"
              data={metrics.profitMargin}
              dataKey="value"
              xAxisKey="date"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
